import { useCreateViewMutation, useUpdateViewMutation } from "@api/view.api";
import { PersistableView, PersistedView } from "@common/types/views/views";
import { Form, FormikProvider, useFormik } from "formik";
import React, { useCallback, useEffect, useRef } from "react";
import { z } from "zod";
import { useViewScope } from "@hooks/view/useViewScope";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import {
    StandardModal,
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";

export type UpdateViewModalMode = "save" | "rename" | "duplicate";

interface Props {
    show: boolean;
    mode: UpdateViewModalMode;
    viewToSave: PersistableView | PersistedView;
    existingView?: PersistedView;
    onClose(): void;
    onSuccess(updatedView: PersistedView): void;
}

const updateViewFormSchema = z.object({
    name: z.string().trim().min(1, "View name is required"),
});

type UpdateViewFormValues = z.infer<typeof updateViewFormSchema>;

const fields = createFormikFields<UpdateViewFormValues>();

const getModalConfig = (mode: UpdateViewModalMode) => {
    switch (mode) {
        case "save":
            return {
                title: "Save View",
                buttonText: "Save View",
                placeholder: "Enter view name...",
            };
        case "rename":
            return {
                title: "Rename View",
                buttonText: "Rename View",
                placeholder: "Enter view name...",
            };
        case "duplicate":
            return {
                title: "Duplicate View",
                buttonText: "Duplicate View",
                placeholder: "Enter name for duplicated view...",
            };
    }
};

export const UpdateViewModal: React.FC<Props> = ({
    show,
    mode,
    viewToSave,
    existingView,
    onClose,
    onSuccess,
}) => {
    const viewScope = useViewScope();
    const createView = useCreateViewMutation(viewScope);
    const updateView = useUpdateViewMutation(viewScope);
    const inputRef = useRef<HTMLInputElement>(null);

    const config = getModalConfig(mode);

    const getCurrentName = () => {
        if (mode === "rename" && existingView) {
            return existingView.name;
        }
        if (mode === "duplicate" && "name" in viewToSave) {
            return `${viewToSave.name} copy`;
        }
        return "";
    };

    const handleSubmit = useCallback(
        async (values: UpdateViewFormValues) => {
            const trimmedName = values.name.trim();

            let result: PersistedView;

            if (mode === "save" || mode === "duplicate") {
                result = await createView.mutateAsync({
                    ...viewToSave,
                    name: trimmedName,
                });
            } else if (mode === "rename" && existingView) {
                result = await updateView.mutateAsync({
                    ...existingView,
                    name: trimmedName,
                });
            } else {
                throw new Error(`Invalid mode or missing data: ${mode}`);
            }
            return result;
        },
        [mode, viewToSave, existingView, createView, updateView],
    );

    const isLoading = createView.isPending || updateView.isPending;

    const formik = useFormik<UpdateViewFormValues>({
        initialValues: {
            name: getCurrentName(),
        },
        validate: validateFormikWithZodSchema(updateViewFormSchema),
        validateOnMount: true,
        onSubmit: submitHelper({
            loading: isLoading,
            handler: async (values) => {
                const result = await handleSubmit(values);
                onSuccess(result);
                onClose();
            },
        }),
    });

    useEffect(() => {
        if (show && inputRef.current) {
            const timer = setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [show]);

    return (
        <StandardModal show={show} onHide={onClose}>
            <StandardModalHeader>{config.title}</StandardModalHeader>
            <StandardModalBody>
                <FormikProvider value={formik}>
                    <Form
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                formik.handleSubmit();
                            }
                        }}
                    >
                        <VStack gap={16}>
                            <fields.Text
                                name="name"
                                label="Name"
                                size="xl"
                                ref={inputRef}
                            />
                            <HStack as="footer" justify="end" gap={12}>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="xl"
                                    onClick={onClose}
                                    disabled={formik.isSubmitting || isLoading}
                                >
                                    Cancel
                                </Button>

                                <ButtonWithLoader
                                    disabled={!formik.isValid}
                                    loading={formik.isSubmitting || isLoading}
                                    type="submit"
                                    variant="primary"
                                    size="xl"
                                >
                                    {config.buttonText}
                                </ButtonWithLoader>
                            </HStack>
                        </VStack>
                    </Form>
                </FormikProvider>
            </StandardModalBody>
        </StandardModal>
    );
};
